import { useEditor } from "@tiptap/react";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { TbArrowLeft } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { memoriesApi } from "../../app-api/memories";
import { selectUser, userActions } from "../../app/reducers/UserSlice";
import Editor from "../../features/memory-editor/Editor";
import MemoryDate from "../../features/memory-editor/MemoryDate";
import Toolbar from "../../features/memory-editor/Toolbar";
import { TipTapStyles } from "../../features/memory-editor/styled";
import { useExtensions } from "../../features/memory-editor/useExtensions";
import { MemoryStatus } from "../../util/functions";
import Button from "../Button";
import toast from "../Toast";
import {
  EditorBody,
  EditorHeader,
  EditorPage,
  TitleError,
  TitleInput,
} from "./styled";
import ModalPopUp from "../ModalPopUp";
import SaveChangesModal from "./SaveChangesModal";

const MemoryEditor = ({ memoryID = null, initialMemory = null, onSuccess }) => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { extensions } = useExtensions();

  const [title, setTitle] = useState(initialMemory?.title ?? "");
  const [occurredDate, setOccurredDate] = useState(
    initialMemory?.date?.occurred
      ? DateTime.fromISO(initialMemory.date.occurred).startOf("day")
      : DateTime.now().startOf("day"),
  );
  const [titleError, setTitleError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditorDirty, setIsEditorDirty] = useState(false);

  const editor = useEditor({
    extensions,
    content: initialMemory?.body?.content ?? null,
    onUpdate: () => setIsEditorDirty(true),
    editorProps: {
      attributes: { spellcheck: "true" },
      transformPastedHTML(html) {
        const regex = new RegExp(/<img.*?>/g);
        const images = regex.exec(html);
        if (images === null) {
          return html;
        }
        toast({
          type: "error",
          message:
            "Images cannot be pasted. Please use the upload button instead.",
          toastId: "copy-paste-image",
        });
        return html.replace(/<img.*?>/g, "");
      },
    },
  });

  const computeIsDirty = useCallback(() => {
    if (isEditorDirty) return true;
    if (title !== (initialMemory?.title ?? "")) return true;
    const initDateISO = initialMemory?.date?.occurred
      ? DateTime.fromISO(initialMemory.date.occurred).startOf("day").toISO()
      : DateTime.now().startOf("day").toISO();
    if (occurredDate.toISO() !== initDateISO) return true;
    return false;
  }, [isEditorDirty, title, occurredDate, initialMemory]);

  const handleBack = useCallback(() => {
    if (computeIsDirty()) {
      setIsModalOpen(true);
      return;
    }
    navigate(-1);
  }, [navigate, computeIsDirty]);

  const handleCancelModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setIsModalOpen(false);
    navigate(-1);
  }, [navigate]);

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError("Title is required.");
      return;
    }
    setTitleError("");

    if (!user?.userID) {
      toast({ message: "Unable to submit. User not loaded.", type: "error" });
      return;
    }

    const dto = {
      title: trimmedTitle,
      content: editor?.getJSON() ?? { type: "doc", content: [] },
      visibility: 1,
      tags: [],
      status: MemoryStatus.Publish,
      author: user.userID,
      occurred: occurredDate.toISO(),
      isSeason: false,
    };

    setIsSubmitting(true);
    try {
      if (memoryID) {
        await memoriesApi.editMemoryById(memoryID, dto);
      } else {
        await memoriesApi.createMemory(dto);
      }
      setIsEditorDirty(false);
      toast({ message: "Chapter saved!", type: "success" });
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/memories");
      }
    } catch {
      toast({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(userActions.principal());
  }, [dispatch]);

  return (
    <EditorPage>
      <TipTapStyles />
      <EditorHeader>
        <Button
          style={{ width: "40px", minWidth: "unset", height: "40px" }}
          variant="tertiary"
          icon
          type="button"
          onClick={handleBack}
        >
          <TbArrowLeft
            size={20}
            style={{ minHeight: "20px", minWidth: "20px" }}
          />
        </Button>
        <ModalPopUp
          style={{
            content: {
              width: "calc(100% - 32px)",
              maxWidth: "320px",
              boxSizing: "border-box",
            },
            overlay: {
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              backdropFilter: "blur(4px)",
            },
          }}
          isOpen={isModalOpen}
          onRequestClose={handleCancelModal}
        >
          <SaveChangesModal
            onSave={handleSubmit}
            onDiscard={handleDiscard}
            onCancel={handleCancelModal}
            isSaving={isSubmitting}
          />
        </ModalPopUp>
        <TitleInput
          placeholder="Give your chapter a title..."
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (titleError) setTitleError("");
          }}
        />
        <Button
          variant="primary"
          type="button"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </EditorHeader>
      {titleError && <TitleError>{titleError}</TitleError>}
      <EditorBody>
        <MemoryDate date={occurredDate} setDate={setOccurredDate} />
        <Toolbar editor={editor} />
        <Editor editor={editor} />
      </EditorBody>
    </EditorPage>
  );
};

export default MemoryEditor;
